import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { HRM_PERMISSION_CATALOG } from '@/constant/hr/permissions';
import { useRolePermissions, useSaveRolePermissions } from '../../hooks/use-roles';
import { OrganizationRoleRow } from '../../types/role.types';

export interface RolePermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: OrganizationRoleRow | null;
}

/** The catalog grouped by module, in catalog order. */
const PERMISSION_GROUPS = HRM_PERMISSION_CATALOG.reduce<
  { group: string; label: string; permissions: typeof HRM_PERMISSION_CATALOG }[]
>((groups, permission) => {
  const last = groups[groups.length - 1];
  if (last?.group === permission.resourceGroup) last.permissions.push(permission);
  else
    groups.push({
      group: permission.resourceGroup,
      label: permission.name.split(':')[0],
      permissions: [permission],
    });
  return groups;
}, []);

/**
 * The app's permissions a role grants in the current organization, module by module. A read-only
 * role (Head of Department, workflow-operator) is shown read-only.
 */
export const RolePermissionsDialog = ({ open, onOpenChange, row }: RolePermissionsDialogProps) => {
  const { t } = useTranslation();
  const slug = open && row ? row.role.slug : null;
  const readOnly = row?.readOnly ?? true;
  const { data: granted, isLoading } = useRolePermissions(slug);
  const { mutateAsync: savePermissions, isPending } = useSaveRolePermissions();
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setSelected(granted ?? []);
    if (!open) setSearch('');
  }, [open, granted]);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return PERMISSION_GROUPS;
    return PERMISSION_GROUPS.map((group) => ({
      ...group,
      permissions: group.permissions.filter((permission) =>
        `${permission.name} ${permission.description}`.toLowerCase().includes(term)
      ),
    })).filter((group) => group.permissions.length > 0);
  }, [search]);

  const toggle = (resources: string[], checked: boolean) =>
    setSelected((current) =>
      checked
        ? [...new Set([...current, ...resources])]
        : current.filter((item) => !resources.includes(item))
    );

  const handleSave = async () => {
    if (!row) return;
    try {
      await savePermissions({ slug: row.role.slug, resources: selected });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t('ROLE_PERMISSIONS')}: {row?.role.name}
          </DialogTitle>
          <DialogDescription>
            {t(readOnly ? 'ROLE_PERMISSIONS_READ_ONLY' : 'ROLE_PERMISSIONS_DESCRIPTION')}
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder={t('SEARCH')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          {isLoading ? (
            <Skeleton className="h-64 w-full rounded-md" />
          ) : (
            groups.map((group) => {
              const resources = group.permissions.map((permission) => permission.resource);
              const all = resources.every((resource) => selected.includes(resource));
              const some = !all && resources.some((resource) => selected.includes(resource));
              return (
                <div key={group.group} className="rounded-md border p-3">
                  <label className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <Checkbox
                      disabled={readOnly}
                      checked={all ? true : some ? 'indeterminate' : false}
                      onCheckedChange={(checked) => toggle(resources, checked === true)}
                    />
                    {group.label}
                  </label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {group.permissions.map((permission) => (
                      <label
                        key={permission.resource}
                        className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted/50"
                      >
                        <Checkbox
                          className="mt-0.5"
                          disabled={readOnly}
                          checked={selected.includes(permission.resource)}
                          onCheckedChange={(checked) =>
                            toggle([permission.resource], checked === true)
                          }
                        />
                        <span className="min-w-0">
                          <span className="block">{permission.name.split(': ')[1]}</span>
                          <span className="block text-xs text-muted-foreground">
                            {permission.description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t(readOnly ? 'CLOSE' : 'CANCEL')}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} loading={isPending} disabled={isPending || isLoading}>
              {t('SAVE_CHANGES')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
